import React, { FC, useCallback, useEffect, useState } from "react";
import moment from "moment";

import { CreateAlert } from "../../modules";
import { AppLogService } from "../../services/app-log";

/**
 * Màn "Lỗi phát" (A6-FE): 3 màn trong 1 trang — Tổng quan · Danh sách máy · Chi tiết máy.
 * Số liệu theo NGÀY LỖI (giờ Việt Nam); mỗi bài lỗi tính 1 lần (bỏ lần thử lại).
 */

export const CATEGORIES: Record<string, string> = {
  YT_BOT: "YouTube chặn (nghi bot)",
  VIDEO_GONE: "Video không còn",
  LINK_DENIED: "Link hết hạn / bị từ chối",
  NO_LINK: "Không lấy được link",
  NETWORK: "Mạng quán",
  MEDIA: "Lỗi file / giải mã",
  OTHER: "Khác",
};

const SOURCES: Record<string, string> = {
  youtube: "YouTube",
  soundcloud: "SoundCloud",
  mixcloud: "Mixcloud",
  server: "Nhạc kho",
  khac: "Khác",
};

const today = () => moment().format("YYYY-MM-DD");
const fmtTime = (v?: string) => (v ? moment(v).format("HH:mm:ss") : "--");
const fmtDateTime = (v?: string) => (v ? moment(v).format("HH:mm DD/MM") : "--");
const fmtRate = (rate: number | null, estimated?: boolean) =>
  rate === null || rate === undefined
    ? "—"
    : `${(Math.round(rate * 1000) / 10).toString().replace(".", ",")}%${estimated ? " (ước tính)" : ""}`;
const fmtMs = (ms?: number) => {
  if (ms === null || ms === undefined) return "";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const CategoryChip: FC<{ value: string }> = ({ value }) => (
  <span className={`app-log-chip cat-${value}`}>{CATEGORIES[value] || value}</span>
);

const showError = (error: any) =>
  CreateAlert({ type: "danger", message: error?.message || "Không tải được dữ liệu" });

type Tab = "summary" | "devices" | "detail";

const AppLogs: FC = () => {
  const [tab, setTab] = useState<Tab>("summary");
  // ?date=yyyy-mm-dd — nút "Mở trang Lỗi phát" trong báo cáo Telegram mở đúng ngày được báo cáo
  const [date, setDate] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("date");
    return q && /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : today();
  });
  const [device, setDevice] = useState<{ mac: string; code?: string } | null>(null);

  const openDevice = (mac: string, code?: string) => {
    setDevice({ mac, code });
    setTab("detail");
  };

  return (
    <div className="app-logs-container">
      <div className="title">Lỗi phát</div>

      <div className="app-logs-toolbar">
        <div className="tabs">
          <button className={tab === "summary" ? "active" : ""} onClick={() => setTab("summary")}>
            Tổng quan
          </button>
          <button className={tab === "devices" ? "active" : ""} onClick={() => setTab("devices")}>
            Danh sách máy
          </button>
          <button className={tab === "detail" ? "active" : ""} disabled={!device} onClick={() => setTab("detail")}>
            Chi tiết máy{device ? ` · ${device.code || device.mac}` : ""}
          </button>
        </div>
        <label className="date-picker">
          Ngày
          <input type="date" value={date} max={today()} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </label>
      </div>

      <div className="content">
        {tab === "summary" && <SummaryView date={date} onOpenDevice={openDevice} />}
        {tab === "devices" && <DevicesView date={date} onOpenDevice={openDevice} />}
        {tab === "detail" && device && <DetailView date={date} mac={device.mac} />}
      </div>

      <div className="app-logs-note">
        Số liệu theo ngày lỗi, giờ Việt Nam. Log về khi quán bật box nên chậm khoảng 1 ngày. Mỗi bài lỗi tính 1 lần (không
        tính các lần app tự thử lại).
      </div>
    </div>
  );
};

// ============================ Màn 1 · Tổng quan ============================
const SummaryView: FC<{ date: string; onOpenDevice: (mac: string, code?: string) => void }> = ({ date, onOpenDevice }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    AppLogService.summary(date)
      .then(setData)
      .catch(showError)
      .finally(() => setLoading(false));
  }, [date]);

  if (loading && !data) return <div className="app-logs-empty">Đang tải…</div>;
  if (!data) return null;

  const top = data.byCategory?.[0];
  const change = data.prevTotalErrors > 0 ? (data.totalErrors - data.prevTotalErrors) / data.prevTotalErrors : null;
  const maxCat = Math.max(1, ...(data.byCategory || []).map((c: any) => c.count));

  return (
    <div className={loading ? "is-loading" : ""}>
      <div className="app-logs-tiles">
        <div className="tile">
          <span className="k">Tổng lỗi</span>
          <span className="v">{data.totalErrors}</span>
          <span className="d">Tỉ lệ lỗi {fmtRate(data.errorRate, data.errorRateEstimated)}</span>
        </div>
        <div className="tile">
          <span className="k">Số máy gặp lỗi</span>
          <span className="v">{data.deviceCount}</span>
        </div>
        <div className="tile">
          <span className="k">Nhóm lỗi nhiều nhất</span>
          <span className="v small">{top ? <CategoryChip value={top.category} /> : "—"}</span>
          {top && (
            <span className="d">
              {top.count} lỗi · {Math.round((top.count / Math.max(1, data.totalErrors)) * 100)}%
            </span>
          )}
        </div>
        <div className="tile">
          <span className="k">So với hôm trước</span>
          <span className={`v ${change !== null && change > 0 ? "up" : "down"}`}>
            {change === null ? "—" : `${change > 0 ? "↑" : "↓"} ${Math.abs(Math.round(change * 100))}%`}
          </span>
          <span className="d">
            {moment(data.prevDate).format("DD/MM")}: {data.prevTotalErrors} lỗi
          </span>
        </div>
      </div>

      {data.serverErrors > 0 && (
        <div className="app-logs-note warn">
          Máy chủ lấy link YouTube lỗi <b>{data.serverErrors}</b> lần — app tự chuyển sang cách lấy link dự phòng nên
          không tính vào lỗi phát. Xem nguyên nhân ở dòng SERVER_RESOLVER trong Chi tiết máy (thiếu cookies, YouTube chặn IP…).
        </div>
      )}

      {data.totalErrors === 0 ? (
        <div className="app-logs-empty">Ngày {moment(date).format("DD/MM/YYYY")} chưa có lỗi nào được ghi nhận.</div>
      ) : (
        <div className="app-logs-grid">
          <div className="panel">
            <h3>Theo nhóm lỗi</h3>
            {data.byCategory.map((c: any) => (
              <div key={c.category} className="bar-row">
                <CategoryChip value={c.category} />
                <span className="n">{c.count}</span>
                <span className="track">
                  <i className={`cat-${c.category}`} style={{ width: `${(c.count / maxCat) * 100}%` }} />
                </span>
              </div>
            ))}
            <div className="sub">
              Theo nguồn: {data.bySource.map((s: any) => `${SOURCES[s.source] || s.source} ${s.count}`).join(" · ")}
            </div>
          </div>
          <div className="panel">
            <h3>Máy lỗi nhiều nhất</h3>
            <table>
              <tbody>
                {data.topDevices.map((d: any) => (
                  <tr key={d.mac} className="click" onClick={() => onOpenDevice(d.mac, d.code)}>
                    <td>
                      <b>{d.code || d.mac}</b>
                      <span className="sub-line">{d.mac}</span>
                    </td>
                    <td className="r">{d.errorCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel">
            <h3>Bài lỗi nhiều nhất</h3>
            <table>
              <tbody>
                {data.topSongs.map((s: any) => (
                  <tr key={s.songId}>
                    <td>{s.displayTitle}</td>
                    <td className="r">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================ Màn 2 · Danh sách máy ============================
const PAGE_SIZE = 20;

const DevicesView: FC<{ date: string; onOpenDevice: (mac: string, code?: string) => void }> = ({ date, onOpenDevice }) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => setPage(1), [date, search, category]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      AppLogService.devices({ date, searchString: search, category, page, pageSize: PAGE_SIZE })
        .then(setData)
        .catch(showError)
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [date, search, category, page]);

  const totalPage = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className={loading ? "is-loading" : ""}>
      <div className="app-logs-filters">
        <input placeholder="Tìm mã máy hoặc MAC" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Mọi nhóm lỗi</option>
          {Object.keys(CATEGORIES).map((k) => (
            <option key={k} value={k}>
              {CATEGORIES[k]}
            </option>
          ))}
        </select>
        <span className="muted">{data ? `${data.total} máy gặp lỗi` : ""}</span>
      </div>

      <div className="table-wrapper">
        <table className="app-logs-table">
          <thead>
            <tr>
              <th>Máy</th>
              <th>MAC</th>
              <th>Bản app</th>
              <th className="r">Số lỗi</th>
              <th className="r">Tỉ lệ</th>
              <th>Nhóm lỗi chính</th>
              <th>Lỗi gần nhất</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.devices || []).map((d: any) => (
              <tr key={d.mac} className="click" onClick={() => onOpenDevice(d.mac, d.code)}>
                <td>
                  <b>{d.code || "—"}</b>
                  <span className="sub-line">{d.deviceName}</span>
                </td>
                <td className="mono">{d.mac}</td>
                <td>{d.appVersion || "không rõ"}</td>
                <td className="r">{d.errorCount}</td>
                <td className="r" title={d.errorRate === null ? "Bản app chưa gửi số bài đã phát" : ""}>
                  {fmtRate(d.errorRate, d.errorRateEstimated)}
                </td>
                <td>
                  <CategoryChip value={d.mainCategory} />
                </td>
                <td>{fmtDateTime(d.lastFailTime)}</td>
                <td>
                  <span className="link">Xem →</span>
                </td>
              </tr>
            ))}
            {data && !data.devices.length && (
              <tr>
                <td colSpan={8} className="muted">
                  Không có máy nào khớp bộ lọc.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pager page={page} totalPage={totalPage} onChange={setPage} />
    </div>
  );
};

// ============================ Màn 3 · Chi tiết máy ============================
const DetailView: FC<{ date: string; mac: string }> = ({ date, mac }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => setPage(1), [date, mac]);

  useEffect(() => {
    setLoading(true);
    AppLogService.logs({ mac, date, page, pageSize: PAGE_SIZE })
      .then(setData)
      .catch(showError)
      .finally(() => setLoading(false));
  }, [mac, date, page]);

  const download = useCallback(async () => {
    try {
      const file = await AppLogService.export(mac, date);
      const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `log_${(data?.code || mac).replace(/[^A-Za-z0-9-]/g, "")}_${date.replace(/-/g, "")}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (error) {
      showError(error);
    }
  }, [mac, date, data]);

  const totalPage = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const first = data?.logs?.[0];

  return (
    <div className={loading ? "is-loading" : ""}>
      <div className="app-logs-device-head">
        <dl>
          <dt>Máy</dt>
          <dd>
            <b>{data?.code || "—"}</b> {first?.deviceName ? `· ${first.deviceName}` : ""}
          </dd>
          <dt>MAC</dt>
          <dd className="mono">{mac}</dd>
          <dt>Bản app</dt>
          <dd>{first?.appVersion || "không rõ"}</dd>
          <dt>Số dòng log</dt>
          <dd>{data?.total ?? "—"}</dd>
        </dl>
        <button className="btn-download" onClick={download} disabled={!data?.total}>
          ⬇ Tải log {moment(date).format("DD/MM")} (JSON)
        </button>
      </div>

      <div className="table-wrapper">
        <table className="app-logs-table">
          <thead>
            <tr>
              <th>Giờ</th>
              <th>Bài</th>
              <th>Nguồn</th>
              <th>Nhóm lỗi</th>
              <th className="r">Lỗi ở</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.logs || []).map((l: any) => (
              <React.Fragment key={l.id}>
                <tr className={l.retryAttempt > 0 ? "retry" : ""}>
                  <td className="mono">{fmtTime(l.failTime)}</td>
                  <td>
                    {l.displayTitle}
                    {l.retryAttempt > 0 && <span className="tag">thử lại lần {l.retryAttempt}</span>}
                    {l.module === "SERVER_RESOLVER" && <span className="tag">máy chủ</span>}
                  </td>
                  <td>{SOURCES[l.source] || l.source || "—"}</td>
                  <td>
                    <CategoryChip value={l.category} />
                  </td>
                  <td className="r mono">
                    {l.positionMs !== null && l.positionMs !== undefined
                      ? `${fmtMs(l.positionMs)} / ${fmtMs(l.durationMs)}`
                      : "Trước khi phát"}
                  </td>
                  <td>
                    <span className="link" onClick={() => setOpen((o) => ({ ...o, [l.id]: !o[l.id] }))}>
                      {open[l.id] ? "Thu gọn" : "Xem thêm"}
                    </span>
                  </td>
                </tr>
                {open[l.id] && (
                  <tr className="detail">
                    <td colSpan={6}>
                      <dl className="kv">
                        <dt>Tiêu đề gốc</dt>
                        <dd>{l.title || "—"}</dd>
                        <dt>Thông báo lỗi</dt>
                        <dd>{l.message || "—"}</dd>
                        {l.errorMessage && (
                          <>
                            <dt>Chi tiết lỗi</dt>
                            <dd>{l.errorMessage}</dd>
                          </>
                        )}
                        <dt>Mã lỗi</dt>
                        <dd className="mono">
                          {l.errorCode ? `${l.errorCode} · ${l.codeName || ""}` : l.module}
                        </dd>
                        <dt>Nguyên nhân</dt>
                        <dd className="mono">{l.cause || "—"}</dd>
                        <dt>Link hết hạn lúc</dt>
                        <dd>
                          {l.linkExpireAt ? moment(l.linkExpireAt).format("HH:mm DD/MM") : "—"}
                          {l.linkExpireAt && l.category === "LINK_DENIED" &&
                            (moment(l.linkExpireAt).isBefore(l.failTime)
                              ? " → link đã hết hạn khi lỗi"
                              : " → link còn hạn, bị từ chối vì lý do khác")}
                        </dd>
                        <dt>Vị trí trong code</dt>
                        <dd className="mono">{l.where || "—"}</dd>
                        <dt>Server nhận lúc</dt>
                        <dd>
                          {fmtDateTime(l.receivedAt)}
                          {l.clockSkew && <span className="tag">đồng hồ box lệch</span>}
                        </dd>
                        {l.stackTrace && (
                          <>
                            <dt>Stack trace</dt>
                            <dd>
                              <pre>{l.stackTrace}</pre>
                            </dd>
                          </>
                        )}
                      </dl>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {data && !data.logs.length && (
              <tr>
                <td colSpan={6} className="muted">
                  Máy này không có log trong ngày {moment(date).format("DD/MM/YYYY")}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pager page={page} totalPage={totalPage} onChange={setPage} />
    </div>
  );
};

const Pager: FC<{ page: number; totalPage: number; onChange: (p: number) => void }> = ({ page, totalPage, onChange }) =>
  totalPage <= 1 ? null : (
    <div className="app-logs-pager">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)}>
        ‹ Trước
      </button>
      <span>
        Trang {page} / {totalPage}
      </span>
      <button disabled={page >= totalPage} onClick={() => onChange(page + 1)}>
        Sau ›
      </button>
    </div>
  );

export default AppLogs;
